import { get } from 'lodash/fp';
import PropTypes from 'prop-types';
import { Suspense, useCallback } from 'react';
import { Route } from 'react-router';
import { compose, setPropTypes } from 'recompose';
import r from '../../../react/services/Replacement';
import * as ROUTES from '../../../routes';
import LoadingIndicatorFull from '../../components/LoadingIndicatorFull';
import NavigationTabs from '../../components/NavigationTabs';
import PageHeader from '../../components/PageHeader';
import PageStatic from '../../components/PageStatic';
import SwitchDefaultRoute from '../../components/SwitchDefaultRoute';
import SwitchErrorRoute from '../../components/SwitchErrorRoute';
import { withAppContext } from '../../hoc/withAppContext';
import { withData } from '../../hoc/withData';
import { useAppContext } from '../../hooks/useAppContext';
import useClippyStateUpdate from '../../hooks/useClippyStateUpdate';
import { getAngularChildRoutes } from '../../services/AngularRoute';
import { isFeatureEnabled } from '../../services/Features';
import { isReviewerInCycles } from '../../services/ReviewCycles';
import { replaceUrl } from '../../services/Router';
import { hasSecondaryReports, isManager } from '../../services/User';
import ActivityStreamTab from '../ActivityStream/tab';
import AnytimeFeedbackTeamInsights from '../AnytimeFeedback/pages/AnytimeFeedbackAdminPage/AnytimeFeedbackInsights/pages/AnytimeFeedbackTeamInsights/lazy';
import MeetingsDashboard from '../MeetingsDashboard/lazy';
import PeopleTab from '../PeopleTab/lazy';

export default compose(
    setPropTypes({
        match: PropTypes.object.isRequired,
    }),
    withAppContext,
    withData({
        resolve: {
            currentUserHasReviewersInCycles: compose(isReviewerInCycles, get('appContext.user')),
        },
        shouldRefetch: (props, nextProps) => props.appContext.user.id !== nextProps.appContext.user.id,
    })
)(TeamPage);

const ActivityStream = () => <ActivityStreamTab mode="TEAM" />;
const MeetingsDashboardTab = () => <MeetingsDashboard mode="TEAM" />;

const Objectives = ({ match }) => {
    const { user } = useAppContext();
    const routes = getAngularChildRoutes([
        ROUTES.YOUR_TEAM_OBJECTIVES_BROWSER,
        ROUTES.YOUR_TEAM_OBJECTIVES_OVERVIEW,
        ROUTES.YOUR_TEAM_OBJECTIVES_SELECT_CYCLE,
    ]);

    if (isManager(user)) {
        routes.reverse();
    }

    return <SwitchDefaultRoute defaultPath={match.path}>{routes}</SwitchDefaultRoute>;
};

const Feedback = () => {
    const routes = getAngularChildRoutes([
        ROUTES.YOUR_TEAM_FEEDBACK_SELECT_CYCLE,
        ROUTES.YOUR_TEAM_FEEDBACK_WITH_CYCLE,
    ]);

    return <SwitchErrorRoute>{routes}</SwitchErrorRoute>;
};

const Reviews = () => {
    const routes = getAngularChildRoutes([ROUTES.YOUR_TEAM_REVIEWS_SELECT_CYCLE, ROUTES.YOUR_TEAM_REVIEWS_WITH_CYCLE]);

    return <SwitchErrorRoute>{routes}</SwitchErrorRoute>;
};

function TeamPage({ match, appContext, currentUserHasReviewersInCycles }) {
    const { user } = appContext;

    const tabs = [
        {
            label: 'Activities',
            link: ROUTES.YOUR_TEAM_ACTIVITY,
            visible: true,
            trackingLabel: 'activities',
        },
        {
            label: r('meeting.name.plural'),
            link: ROUTES.YOUR_TEAM_MEETINGS,
            visible: isManager(user) && !isFeatureEnabled('FEATURE_layoutRefresh'),
            feature: 'meetings',
            trackingLabel: 'meetings',
        },
        {
            label: r('objective.plural.big'),
            link: ROUTES.YOUR_TEAM_OBJECTIVES,
            visible: !isFeatureEnabled('FEATURE_layoutRefresh'),
            feature: 'objective',
            trackingLabel: 'objectives',
        },
        {
            label: r('fr.review.singular.title'),
            link: ROUTES.YOUR_TEAM_FEEDBACK_SELECT_CYCLE,
            visible: isManager(user) && !isFeatureEnabled('FEATURE_layoutRefresh'),
            feature: 'feedback',
            trackingLabel: 'feedback',
        },
        {
            label: isFeatureEnabled('FEATURE_anytimeFeedbackGive') ? r('anyfb.name') : 'Feedback Requests',
            link: ROUTES.YOUR_TEAM_FEEDBACK_REQUESTED,
            visible: isManager(user) && !isFeatureEnabled('FEATURE_layoutRefresh'),
            feature: 'requestFeedback',
            trackingLabel: 'anytime feedback',
        },
        {
            label: r('pr.plural.short'),
            link: ROUTES.YOUR_TEAM_REVIEWS_SELECT_CYCLE,
            visible:
                (isManager(user) || hasSecondaryReports(user) || currentUserHasReviewersInCycles) &&
                !isFeatureEnabled('FEATURE_layoutRefresh'),
            feature: 'review',
            trackingLabel: 'reviews',
        },
        {
            label: 'People',
            link: ROUTES.YOUR_TEAM_USERS,
            visible: true,
            trackingLabel: 'people',
        },
    ];

    const handleClippyStateUpdate = useCallback(nextState => {
        // Go back to param-less route when closing Clippy via its own trigger button.
        if (nextState.isOpen && nextState.isClosing) {
            replaceUrl(ROUTES.BASE + ROUTES.YOUR_TEAM_USERS);
        }
    }, []);

    useClippyStateUpdate(handleClippyStateUpdate);

    return (
        <div>
            <PageStatic>
                <PageHeader title="Your Team" />
            </PageStatic>
            <NavigationTabs tabs={tabs} trackingCategory="team page" />
            <Suspense fallback={<LoadingIndicatorFull />}>
                <SwitchDefaultRoute defaultPath={match.path}>
                    <Route exact path={ROUTES.YOUR_TEAM_ACTIVITY} render={ActivityStream} />
                    {!isFeatureEnabled('FEATURE_layoutRefresh') && (
                        <Route path={ROUTES.YOUR_TEAM_MEETINGS} component={MeetingsDashboardTab} />
                    )}
                    {!isFeatureEnabled('FEATURE_layoutRefresh') && (
                        <Route path={ROUTES.YOUR_TEAM_OBJECTIVES} component={Objectives} />
                    )}
                    {!isFeatureEnabled('FEATURE_layoutRefresh') && (
                        <Route path={ROUTES.YOUR_TEAM_FEEDBACK_SELECT_CYCLE} component={Feedback} />
                    )}
                    {!isFeatureEnabled('FEATURE_layoutRefresh') && (
                        <Route path={ROUTES.YOUR_TEAM_FEEDBACK_REQUESTED} component={AnytimeFeedbackTeamInsights} />
                    )}
                    {!isFeatureEnabled('FEATURE_layoutRefresh') && (
                        <Route path={ROUTES.YOUR_TEAM_REVIEWS_SELECT_CYCLE} component={Reviews} />
                    )}
                    <Route exact path={ROUTES.YOUR_TEAM_USERS} component={PeopleTab} />
                    <Route path={ROUTES.YOUR_TEAM_USERS_USER} exact component={PeopleTab} />
                </SwitchDefaultRoute>
            </Suspense>
        </div>
    );
}
